import React, { useEffect, useState, useRef } from 'react';
import axios from '../../api/axios';
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { toast } from 'react-toastify';
import { TrashIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';

const CreatePurchases = () => {
  const navigate = useNavigate();
  const { purchasesId } = useParams();

  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const [items, setItems] = useState([]); // catalog items with sizes/colors
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [purchasesDate, setPurchasesDate] = useState(new Date());
  const [suppliersOption, setSuppliersOption] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [status, setStatus] = useState('Received'); // Received | Pending | Ordered
  const [reference, setReference] = useState('');

  // Purchase lines
  // Each: { id, name, price, discount, total, quantity, sizes:[{size,quantity}], colors:[{color,quantity}] }
  const [saleItems, setSaleItems] = useState([]);

  const [discountType, setDiscountType] = useState('flat'); // percent | flat
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [otherCharges, setOtherCharges] = useState(0);
  const [note, setNote] = useState('');

  const [banks, setBanks] = useState([]); // [{value,label}]
  const [paymentBank, setPaymentBank] = useState(null);

  const [paidAmount, setPaidAmount] = useState(0);     // existing paid (edit)
  const [newPaidAmount, setNewPaidAmount] = useState(0); // new payment now
  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: "Received", label: "Received" },
    { value: "Pending", label: "Pending" },
    { value: "Ordered", label: "Ordered" },
  ];

  // -----------------------
  // Helpers
  // -----------------------

  // ✅ sizes define quantity; colors are descriptive only
  const recalcLine = (line) => {
    const sizeQty = (line.sizes || []).reduce((s, r) => s + Number(r.quantity || 0), 0);
    const qty = sizeQty; // total always comes from sizes only
    const price = Number(line.price || 0);
    const disc = Number(line.discount || 0);
    return { ...line, quantity: qty, total: qty * (price - disc) };
  };

  // -----------------------
  // Bootstrap
  // -----------------------
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const [itemsRes, suppliersRes, banksRes] = await Promise.all([
          axios.get('/items'),
          axios.get('/suppliers'),
          axios.get('/banks')
        ]);

        setItems(itemsRes.data || []);

        const supplierOption = (suppliersRes.data || []).map(s => ({ value: s.id, label: s.name }));
        setSuppliersOption(supplierOption);

        const bankOption = (banksRes.data || []).map(b => ({ value: b.id, label: b.name }));
        setBanks(bankOption);
        if (bankOption.length > 0) setPaymentBank(bankOption[0].value);

        if (purchasesId) {
          const res = await axios.get(`/purchases/${purchasesId}`);
          const p = res.data;

          setSelectedSupplier(p.supplier_id);
          setStatus(p.status);
          setReference(p.reference);
          setPurchasesDate(new Date(p.purchase_date));
          setOtherCharges(Number(p.other_charges || 0));
          setGlobalDiscount(Number(p.discount || 0));
          setDiscountType(p.discount_type || 'flat');
          setPaidAmount(Number(p.paid_amount || 0));
          setNote(p.note || '');
          setPaymentBank(p.bank_id || bankOption[0]?.value || null);

          // Map purchase → UI line model (sizes control quantity)
          const mapped = (p.items || []).map(line => {
            const fromCatalog = (itemsRes.data || []).find(i => i.id === line.item_id) || {};
            const sizes = Array.isArray(line.sizes) ? line.sizes : [];
            const colors = Array.isArray(line.colors) ? line.colors : [];

            // Seed empty from catalog when editing
            const seededSizes = sizes.length
              ? sizes.map(s => ({ size: s.size, quantity: Number(s.quantity || 0) }))
              : (fromCatalog.sizes || []).map(s => ({ size: s.size, quantity: 0 }));

            const seededColors = colors.length
              ? colors.map(c => ({ color: c.color, quantity: Number(c.quantity || 0) }))
              : (fromCatalog.colors || []).map(c => ({ color: c.color, quantity: 0 }));

            const price = Number(line.price || fromCatalog.purchase_price_last || 0);
            const discount = Number(line.discount || 0);

            return recalcLine({
              id: line.item_id,
              name: line.name || fromCatalog.name,
              price,
              discount,
              sizes: seededSizes,
              colors: seededColors,
            });
          });

          setSaleItems(mapped);
        }
      } catch (err) {
        console.error('Init error:', err);
        toast.error(err?.response?.data?.error || 'Failed to load data');
      }
    };

    bootstrap();
  }, [purchasesId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // -----------------------
  // Item search / add
  // -----------------------
  const handleAddItem = (item) => {
    setSaleItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        // bump first SIZE row (since sizes define quantity)
        const next = { ...exists };
        if (next.sizes?.length) {
          next.sizes = [{ ...next.sizes[0], quantity: Number(next.sizes[0].quantity || 0) + 1 }, ...next.sizes.slice(1)];
        } else {
          next.sizes = [{ size: '', quantity: 1 }];
        }
        return prev.map(l => (l.id === item.id ? recalcLine(next) : l));
      }

      const price = Number(item.purchase_price_last || 0);
      const seedSizes = Array.isArray(item.sizes) && item.sizes.length
        ? item.sizes.map(s => ({ size: s.size, quantity: 0 }))
        : [{ size: '', quantity: 0 }];

      const seedColors = Array.isArray(item.colors) && item.colors.length
        ? item.colors.map(c => ({ color: c.color, quantity: 0 }))
        : [{ color: '', quantity: 0 }];

      const base = {
        id: item.id,
        name: item.name,
        price,
        discount: 0,
        sizes: seedSizes,
        colors: seedColors,
      };
      return [...prev, recalcLine(base)];
    });

    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleSizeChange = (itemId, index, field, value) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const arr = [...(line.sizes || [])];
        arr[index] = { ...arr[index], [field]: field === 'quantity' ? Number(value || 0) : value };
        return recalcLine({ ...line, sizes: arr });
      })
    );
  };

  const handleColorChange = (itemId, index, field, value) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const arr = [...(line.colors || [])];
        arr[index] = { ...arr[index], [field]: field === 'quantity' ? Number(value || 0) : value };
        // ❗ colors do not change totals; recalcLine will ignore color qty for totals
        return recalcLine({ ...line, colors: arr });
      })
    );
  };

  const addSizeRow = (itemId) => {
    setSaleItems(prev =>
      prev.map(line =>
        line.id === itemId
          ? recalcLine({ ...line, sizes: [...(line.sizes || []), { size: '', quantity: 0 }] })
          : line
      )
    );
  };

  const addColorRow = (itemId) => {
    setSaleItems(prev =>
      prev.map(line =>
        line.id === itemId
          ? recalcLine({ ...line, colors: [...(line.colors || []), { color: '', quantity: 0 }] })
          : line
      )
    );
  };

  const updateItem = (id, field, value) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== id) return line;
        const next = { ...line, [field]: Number(value) };
        return recalcLine(next);
      })
    );
  };

  const removeItem = (id) => setSaleItems(prev => prev.filter(i => i.id !== id));

  // -----------------------
  // Totals & Submit
  // -----------------------
  const subtotal = saleItems.reduce((sum, line) => sum + Number(line.total || 0), 0);
  const discountOnAll = discountType === 'percent'
    ? (subtotal * Number(globalDiscount || 0)) / 100
    : Number(globalDiscount || 0);
  const grandTotal = subtotal + Number(otherCharges || 0) - discountOnAll;

  const totalPaying = Number(paidAmount || 0) + Number(newPaidAmount || 0);
  const dueAmount = Math.max(grandTotal - totalPaying, 0).toFixed(2);
  const paymentStatus = totalPaying >= grandTotal ? 'paid' : totalPaying > 0 ? 'partial' : 'unpaid';

  const validateForm = () => {
    const newErrors = {};
    if (!selectedSupplier) newErrors.supplier = 'Supplier is required';
    if (!status) newErrors.status = 'Status is required';
    if (!paymentBank) newErrors.paymentBank = 'Payment Bank is required';
    if (saleItems.length === 0) newErrors.items = 'Add at least one item';
    // also ensure some size qty > 0 overall
    const anyQty = saleItems.some(l => (l.sizes || []).some(s => Number(s.quantity) > 0));
    if (!anyQty) newErrors.items = 'Enter quantity in Sizes (colors don’t affect quantity).';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        supplier_id: selectedSupplier,
        status,
        reference,
        note,
        purchase_date: purchasesDate,
        bank_id: paymentBank,
        other_charges: Number(otherCharges || 0),
        discount: Number(globalDiscount || 0),
        paid_amount: Number(newPaidAmount || 0),
        payment_status: paymentStatus,
        items: saleItems.map(line => {
          const base = {
            item_id: line.id,
            price: Number(line.price || 0),
            discount: Number(line.discount || 0),
            total: Number(line.total || 0),
          };
          const sizes = (line.sizes || [])
            .filter(s => (s.size || '').trim().length)
            .map(s => ({ size: s.size.trim(), quantity: Number(s.quantity || 0) }));
          const colors = (line.colors || [])
            .filter(c => (c.color || '').trim().length)
            .map(c => ({ color: c.color.trim(), quantity: Number(c.quantity || 0) }));
          return { ...base, ...(sizes.length ? { sizes } : {}), ...(colors.length ? { colors } : {}) };
        })
      };

      const apiUrl = purchasesId ? `/purchases/${purchasesId}` : '/purchases';
      const res = purchasesId ? await axios.put(apiUrl, payload) : await axios.post(apiUrl, payload);

      if (res.data?.success) {
        toast.success(res.data?.message || `Purchases ${purchasesId ? 'updated' : 'created'} successfully!`);
        navigate('/purchases-list');
      } else {
        toast.error(res.data?.message || 'Failed to save purchase');
      }
    } catch (err) {
      console.error('Save purchase error:', err);
      toast.error(err?.response?.data?.error || err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = searchTerm.length >= 1
    ? items.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  // -----------------------
  // UI
  // -----------------------
  return (
    <div className="relative p-4 max-w-6xl mx-auto bg-white rounded">
      <h2 className="text-xl font-bold mb-4">Purchases — Add/Update</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Top */}
        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="font-semibold text-sm">Purchases Date</label>
            <Flatpickr
              value={purchasesDate}
              className="border w-full rounded"
              onChange={(date) => setPurchasesDate(date[0])}
            />
          </div>
          <div>
            <label className="font-semibold text-sm">Supplier Name *</label>
            <Select
              name="Supplier"
              options={suppliersOption}
              placeholder="Select Supplier"
              value={suppliersOption.find(c => c.value === selectedSupplier) || null}
              onChange={(e) => {
                setSelectedSupplier(e.value);
                if (errors.supplier) setErrors(prev => ({ ...prev, supplier: null }));
              }}
              classNamePrefix="react-select"
              maxMenuHeight={150}
              className={errors.supplier ? 'border border-red-500' : ''}
            />
            {errors.supplier && <p className="text-red-500 text-xs">{errors.supplier}</p>}
          </div>
          <div>
            <label className="font-semibold text-sm">Status</label>
            <Select
              name="status"
              options={statusOptions}
              placeholder="Select Status"
              value={statusOptions.find(v => v.value === status) || null}
              onChange={(e) => {
                setStatus(e.value);
                if (errors.status) setErrors(prev => ({ ...prev, status: null }));
              }}
              classNamePrefix="react-select"
              maxMenuHeight={150}
              className={errors.status ? 'border border-red-500' : ''}
            />
            {errors.status && <p className="text-red-500 text-xs">{errors.status}</p>}
          </div>
          <div>
            <label className="font-semibold text-sm">Reference No</label>
            <input
              type="text"
              value={reference}
              placeholder="Enter reference"
              onChange={(e) => setReference(e.target.value)}
              className="border w-full rounded"
            />
          </div>
        </div>

        {/* Item Search */}
        <div className="mb-2 relative" ref={dropdownRef}>
          <input
            type="text"
            placeholder="Enter Item name/Itemcode"
            className="border w-full rounded"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(e.target.value.length > 0);
            }}
          />
          {errors.items && <p className="text-red-500 text-xs mt-1">{errors.items}</p>}

          {showDropdown && filteredItems.length > 0 && (
            <div className="absolute z-50 bg-white shadow w-full border mt-1 rounded max-h-48 overflow-y-auto">
              {filteredItems.map(item => (
                <div
                  key={item.id}
                  className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                  onClick={() => handleAddItem(item)}
                >
                  <span>{item.name}</span>
                  <span className="text-gray-500">{item.purchase_price_last} BDT</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lines */}
        {saleItems.length > 0 && (
          <table className="w-full mt-4 border text-xs">
            <thead className="bg-blue-800 text-white">
              <tr>
                <th className="border px-2 py-1">Item Name</th>
                <th className="border px-2 py-1">Sizes (define quantity)</th>
                <th className="border px-2 py-1">Colors (descriptive only)</th>
                <th className="border px-2 py-1">Unit Price</th>
                <th className="border px-2 py-1">Discount</th>
                <th className="border px-2 py-1">Total Amount</th>
                <th className="border px-2 py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {saleItems.map(item => (
                <tr key={item.id}>
                  <td className="border px-2 py-1 text-blue-600 font-medium">{item.name}</td>

                  {/* Sizes editor */}
                  <td className="border px-2 py-1">
                    {(item.sizes || []).map((s, i) => (
                      <div key={i} className="flex gap-1 items-center mb-1">
                        <input
                          type="text"
                          value={s.size}
                          onChange={e => handleSizeChange(item.id, i, 'size', e.target.value)}
                          className="w-20 border px-1 rounded"
                          placeholder="Size"
                        />
                        <input
                          type="number"
                          value={s.quantity}
                          onChange={e => handleSizeChange(item.id, i, 'quantity', e.target.value)}
                          className="w-20 border px-1 rounded"
                          placeholder="Qty"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-xs text-blue-600 mt-1"
                      onClick={() => addSizeRow(item.id)}
                    >
                      + Add Size
                    </button>
                  </td>

                  {/* Colors editor */}
                  <td className="border px-2 py-1">
                    {(item.colors || []).map((c, i) => (
                      <div key={i} className="flex gap-1 items-center mb-1">
                        <input
                          type="text"
                          value={c.color}
                          onChange={e => handleColorChange(item.id, i, 'color', e.target.value)}
                          className="w-24 border px-1 rounded"
                          placeholder="Color"
                        />
                        <input
                          type="number"
                          value={c.quantity}
                          onChange={e => handleColorChange(item.id, i, 'quantity', e.target.value)}
                          className="w-20 border px-1 rounded"
                          placeholder="Qty"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-xs text-blue-600 mt-1"
                      onClick={() => addColorRow(item.id)}
                    >
                      + Add Color
                    </button>
                    <div className="text-[10px] text-gray-500 mt-1">
                      Note: Color quantities don't change totals; they're just for tracking split.
                    </div>
                  </td>

                  {/* Price/Discount/Total */}
                  <td className="border px-2 py-1">
                    <input
                      type="number"
                      value={item.price}
                      onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                      className="border p-1 rounded text-center w-24"
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <input
                      type="number"
                      value={item.discount}
                      onChange={(e) => updateItem(item.id, 'discount', e.target.value)}
                      className="border p-1 rounded text-center w-24"
                    />
                  </td>
                  <td className="border px-2 py-1 text-right font-semibold">
                    {Number(item.total || 0).toFixed(2)}
                  </td>
                  <td className="border px-2 py-1 text-center">
                    <TrashIcon onClick={() => removeItem(item.id)} className="h-4 w-4 text-red-500 cursor-pointer" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Charges & Payment */}
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="font-semibold text-sm">Other Charges</label>
            <input
              type="number"
              value={otherCharges}
              placeholder="Enter other charge"
              onChange={(e) => setOtherCharges(Number(e.target.value || 0))}
              className="border w-full rounded"
            />
          </div>
          <div>
            <label className="font-semibold text-sm">Discount on All</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={globalDiscount}
                onChange={(e) => setGlobalDiscount(Number(e.target.value || 0))}
                className="w-full border rounded"
              />
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                className="border rounded"
              >
                <option value="percent">%</option>
                <option value="flat">Flat</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-semibold text-sm">Payment Bank</label>
            <Select
              name="paymentBank"
              options={banks}
              value={banks.find(v => v.value === paymentBank) || null}
              placeholder="Select Bank"
              classNamePrefix="react-select"
              maxMenuHeight={150}
              onChange={(e) => {
                setPaymentBank(e.value);
                if (errors.paymentBank) setErrors(prev => ({ ...prev, paymentBank: null }));
              }}
              className={errors.paymentBank ? 'border border-red-500' : ''}
            />
            {errors.paymentBank && <p className="text-red-500 text-xs">{errors.paymentBank}</p>}
          </div>
        </div>

        {/* Summary */}
        <div className="text-right mt-6 space-y-1">
          <div>Subtotal: <strong>{subtotal.toFixed(2)}</strong></div>
          <div>Other Charges: <strong>{Number(otherCharges || 0).toFixed(2)}</strong></div>
          <div>Discount: <strong>{discountOnAll.toFixed(2)}</strong></div>
          <div>Due Amount: <strong>{Math.max(grandTotal - (Number(paidAmount || 0) + Number(newPaidAmount || 0)), 0).toFixed(2)} BDT</strong></div>
          <div className="text-lg font-bold">Grand Total: <strong>{grandTotal.toFixed(2)} BDT</strong></div>
        </div>

        {/* Paid & Note */}
        <div className="mt-4">
          <label className="font-semibold text-sm">Paid Amount</label>
          <input
            type="number"
            className="border rounded w-full"
            onChange={(e) => setNewPaidAmount(Number(e.target.value || 0))}
          />
          <p className="text-sm text-gray-600 mt-1">
            Payment Status: <b>{paymentStatus}</b>
          </p>
        </div>
        <div>
          <label className="font-semibold text-sm">Note</label>
          <textarea
            className="w-full border rounded"
            rows="2"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4 mt-4">
          <button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-1 rounded">
            Save
          </button>
          <button
            type="button"
            onClick={() => navigate('/purchases-list')}
            className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-1 rounded"
          >
            Close
          </button>
        </div>
      </form>

      <Loader show={loading} />
    </div>
  );
};

export default CreatePurchases;
